import { loginSchema } from "@rmnlng/shared";
import { useForm } from "@tanstack/react-form";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { FieldError } from "@/components/FieldError";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLogin, useMe } from "@/lib/auth";
import { useErrorMessage } from "@/lib/error-message";
import { useT } from "@/lib/i18n";

type LocationState = {
  from?: { pathname?: string };
  // One-shot flash message piggybacked on react-router state from
  // `/reset-password`'s success path ("Password updated — sign in
  // again."). Read on first render, then cleared via `replace` so a
  // back-button hop doesn't re-show it.
  flash?: string;
} | null;

export function LoginPage() {
  const { data: user } = useMe();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const errorMessage = useErrorMessage();

  // One-shot flash banner from the password-reset success redirect.
  // Captured into local state on mount and cleared from router state
  // immediately so a back-button hop or a re-render doesn't replay
  // it. Calling `navigate` during render would loop, so the clear
  // happens in the effect.
  const [flash] = useState<string | null>(() => (location.state as LocationState)?.flash ?? null);
  useEffect(() => {
    if (flash) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // Mount-only intent — the flash is captured on first render and we
    // don't want subsequent location changes to retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    defaultValues: { email: "", password: "" },
    validators: { onChange: loginSchema },
    onSubmit: async ({ value }) => {
      await login.mutateAsync(value);
      const from = (location.state as LocationState)?.from?.pathname ?? "/";
      navigate(from, { replace: true });
    },
  });

  if (user) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-dvh w-full items-stretch justify-center bg-bg">
      <div className="flex w-full max-w-[440px] flex-col px-7 pt-12 pb-8">
        {/* Brand mark */}
        <div className="mb-12">
          <Logo className="size-12" />
        </div>

        <h1 className="mb-2.5 text-[32px] font-bold leading-[1.1] tracking-[-0.03em]">{t("login.welcome_back")}</h1>
        <p className="mb-9 text-[15px] leading-[1.5] text-text-3">{t("login.subtitle")}</p>

        {/* Success flash from /reset-password — borderless info pane,
            green to mark "this was a positive outcome", auto-clears
            on next nav (see effect above). */}
        {flash && (
          <div
            role="status"
            className="mb-6 rounded-lg bg-[color:var(--good-bg,#ecfdf5)] px-4 py-3 text-[13px] text-[color:var(--good,#047857)]"
          >
            {flash}
          </div>
        )}

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          noValidate
        >
          <form.Field name="email">
            {(field) => (
              <div>
                <label htmlFor={field.name} className="mb-1.5 block text-[12px] font-medium text-text-2">
                  {t("login.email")}
                </label>
                <Input
                  id={field.name}
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          <form.Field name="password">
            {(field) => (
              <div>
                <div className="mb-1.5 flex items-baseline justify-between">
                  <label htmlFor={field.name} className="block text-[12px] font-medium text-text-2">
                    {t("login.password")}
                  </label>
                  {/* Inline forgot-password link — sits next to the
                      password label per the updated design in
                      `design/hifi/login.jsx`. Anchored to the field
                      it relates to, not buried in a footer. */}
                  <Link to="/forgot-password" className="text-[12px] font-medium text-[color:var(--accent)]">
                    {t("login.forgot_password")}
                  </Link>
                </div>
                <Input
                  id={field.name}
                  type="password"
                  autoComplete="current-password"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="qwerty123"
                />
                <FieldError errors={field.state.meta.errors} touched={field.state.meta.isTouched} />
              </div>
            )}
          </form.Field>

          {login.error && (
            <p className="text-[13px] text-[color:var(--again)]" role="alert">
              {/* `useErrorMessage` returns the localized copy for any
                  recognised auth error code, falling back to a
                  generic "something went wrong" — never leaks the raw
                  English HTTP message into the UI. */}
              {errorMessage(login.error)}
            </p>
          )}

          <form.Subscribe
            selector={(s) => ({
              canSubmit: s.canSubmit,
              isSubmitting: s.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button type="submit" size="block" className="mt-3" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? t("login.submitting") : t("login.submit")}
              </Button>
            )}
          </form.Subscribe>
        </form>

        <div className="mt-auto pt-6 text-center text-[13px] text-text-3">
          {t("login.need_account")}{" "}
          <Link to="/signup" className="font-medium text-[color:var(--accent)]">
            {t("login.sign_up")}
          </Link>
        </div>
      </div>
    </div>
  );
}
